import moment from 'moment-timezone'
import LeavePermission from '@/components/requests/leave/LeavePermission'

export default class LeaveDetailsMicrocopy {
  constructor(leave, viewer, allowance) {
    this.leave = leave
    this.viewer = viewer
    this.leaveOwnerAllowance = allowance
  }

  get microcopy() {
    if (
      this.leave.type.only_admin &&
      !this.viewer.isAdmin() &&
      this.leave.isCancellable()
    ) {
      return this._makeMicrocopyForAdminOnlyLeave()
    }

    if (
      this.leave.isCancellable() &&
      this.leave.cancellation?.planLimitationWarning
    ) {
      return this.leave.cancellation.planLimitationWarning.message
    }

    if (
      this.leave.isCancellable() &&
      !LeavePermission.canCancel(this.leave, this.viewer)
    ) {
      if (this._isTheViewerOwner()) {
        return this._makeMicrocopyForBlockedOwner()
      }

      if (LeavePermission.isApproverOrAdmin(this.leave, this.viewer)) {
        return this._makeMicrocopyForBlockedSuperior()
      }

      return ''
    }

    if (
      this.leave.isCancellable() &&
      LeavePermission.canCancel(this.leave, this.viewer)
    ) {
      return this._isTheViewerOwner()
        ? this._makeMicrocopyForCancellableOwner()
        : this._makeMicrocopyForCancellableSuperior()
    }

    return ''
  }

  _makeMicrocopyForBlockedOwner() {
    if (this.leave.cancellation?.message) {
      return this.leave.cancellation.message
    }

    return `It is not possible to update or cancel a request which has already been taken, please contact
       **${this.viewer.superiorNames}**
      to assist`
  }

  _makeMicrocopyForCancellableOwner() {
    const allowanceMicrocopy = this._makeMicrocopyForLeaveOwner()

    if (this.leave.cancellation?.cancelUntil) {
      return `Can cancel until **${this._formatDeadline()}**. ${allowanceMicrocopy}`
    }

    return allowanceMicrocopy
  }

  _makeMicrocopyForLeaveOwner() {
    if (!this.leave.hasAffectToEmploymentAllowances()) {
      return `Cancelling this request won't affect **your** allowance because ${this.leave.getTypeName()} does not deduct`
    }

    if (this._isLeaveOwnerAllowanceUnlimited()) {
      return `Cancelling this request won't affect ** your ** allowance because it is unlimited`
    }

    return `Cancelling this request will credit ** your ** allowance`
  }

  _makeMicrocopyForBlockedSuperior() {
    if (this.leave.cancellation?.message) {
      return this.leave.cancellation.message
    }

    return `You're not allowed to cancel this request based on your company's cancellation policy`
  }

  _makeMicrocopyForCancellableSuperior() {
    const allowanceMicrocopy = this._makeMicrocopyForSuperior()

    if (this.leave.cancellation?.cancelUntil) {
      return `Can cancel until **${this._formatDeadline()}**. ${allowanceMicrocopy}`
    }

    return allowanceMicrocopy
  }

  _makeMicrocopyForSuperior() {
    let action =
      this.viewer.isApproverOf(this.leave.owner_id) && this.leave.isRequested()
        ? 'Declining'
        : 'Cancelling'

    if (!this.leave.hasAffectToEmploymentAllowances()) {
      return `
          ${action} this request won't affect the allowance for
          ** ${this._getLeaveOwnerName()} **
          because ${this.leave.getTypeName()} does not deduct
        `
    }

    if (this._isLeaveOwnerAllowanceUnlimited()) {
      return `
          ${action} this request won't affect the allowance for
          ** ${this._getLeaveOwnerName()} **
          because it's unlimited
        `
    }

    return `${action} this request will credit the allowance for ** ${this._getLeaveOwnerName()} **`
  }

  _formatDeadline() {
    // cancelUntil is a date-only string already resolved to the leave's timezone
    // by the backend, so treat it as a plain calendar date — converting it through
    // .tz() would re-interpret it as an instant and shift the day in browsers whose
    // timezone is ahead of the leave's.
    return moment(this.leave.cancellation.cancelUntil, 'YYYY-MM-DD').format(
      'D MMMM YYYY'
    )
  }

  _getLeaveOwnerName() {
    if (this._isTheViewerOwner()) {
      return 'you'
    }
    return this.leave.owner.full_name
  }

  _isTheViewerOwner() {
    return this.viewer.id === this.leave.owner_id
  }

  _isLeaveOwnerAllowanceUnlimited() {
    if (!this.leaveOwnerAllowance) {
      return true
    }

    return this.leaveOwnerAllowance.isUnlimited()
  }

  _makeMicrocopyForAdminOnlyLeave() {
    const admins = this.viewer.company?.admins || []

    const adminNames = admins.map(a => a.full_name).join(', ')

    return `This leave type can only be managed by admins. To make changes or cancel this leave, contact: **${adminNames}**`
  }
}
